import { RefObject, useCallback, useEffect, useRef } from 'react';

export type UseComposedPathOptions = {
  isDisabled?: boolean;
  ref: RefObject<Element>;
};

/**
 * `useOverlay` from react-aria closes an overlay when it detects clicks "outside"
 * of it.  This is done by adding an event handler to `document` and checking whether
 * or not the event target is within the overlay.  This does not work in all cases -
 * for example, when running inside of a shadow dom, the event target as seen by a
 * handler added to document is always the shadow root itself, regardless of what
 * was clicked within the shadow root. This is referred to as shadow dom event
 * "retargeting".
 *
 * In these cases, it's useful to check the "composed path", which is a function on
 * an event that creates an array of all elements that the event will bubble through.
 * If the overlay is within the composed path, then the click must have happened within
 * the overlay.
 *
 * `useOverlay` exposes a prop called `shouldCloseOnInteractOutside` which is a function
 * that receives an element and returns a boolean indicating whether or not the overlay
 * should close.  The element passed to the function is the event target, the idea being
 * that the target could be inspected an a decision about closing the overlay could be made.
 * Because the shadow dom retargets the event, the event target isn't helpful as it's always
 * the shadow root.
 *
 * The idea behind this hook is to capture the event's composed path so that it can
 * be inspected within the `shouldCloseOnInteractOutside` prop (see usage in
 * `UnstyledPopoverOverlay`).
 *
 * A lot of the logic of this hook was copied from
 * https://github.com/adobe/react-spectrum/blob/6a503b715e0dbbf92038cd7f08b1bcdde4c78e82/packages/%40react-aria/interactions/src/useInteractOutside.ts
 *
 * react-aria does not support useOverlay in the shadow dom, as seen here https://github.com/adobe/react-spectrum/issues/3970
 * A PR for support was submitted https://github.com/adobe/react-spectrum/pull/4526, but
 * closed because the team wants to support the shadow dom in a more proactive, planned
 * manner https://github.com/adobe/react-spectrum/issues/1472#issuecomment-1562081880
 */
/*
 * HACK - this depends on a specific order of event handlers invoked by the browser.
 * Both this hook and react-aria's `useInteractOutside` (via `useOverlay`) will add
 * event listeners to the document.  In order for this code to work, it has to run before
 * react-aria's event handler.  All of the popular modern browsers invoke handlers on the
 * same element in the order they are added, so this hook should be called bofore react-aria's
 * useOverlay.
 *
 * ** THIS CODE MIGHT NOT WORK FOR OLDER / NICHE BROWSERS **
 */
export default function useComposedPath({
  isDisabled,
  ref,
}: UseComposedPathOptions) {
  const composedPathRef = useRef<EventTarget[]>([]);

  const recordComposedPath = useCallback((e: Event) => {
    composedPathRef.current = e.composedPath();
  }, []);

  const clearComposedPath = useCallback(() => {
    composedPathRef.current = [];
  }, []);

  useEffect(() => {
    let isPointerDown = false;
    let ignoreEmulatedMouseEvents = false;

    if (!isDisabled) {
      const doc = ref.current?.ownerDocument ?? document;

      const handlePointerDown = (e: Event) => {
        recordComposedPath(e);
        isPointerDown = true;
      };

      const handlePointerUp = (e: PointerEvent) => {
        if (isPointerDown) {
          recordComposedPath(e);
        }
        isPointerDown = false;
      };

      const handleMouseUp = (e: MouseEvent) => {
        if (ignoreEmulatedMouseEvents) {
          ignoreEmulatedMouseEvents = false;
        } else if (isPointerDown) {
          recordComposedPath(e);
        }
        isPointerDown = false;
      };

      const handleTouchEnd = (e: TouchEvent) => {
        ignoreEmulatedMouseEvents = true;
        if (isPointerDown) {
          recordComposedPath(e);
        }

        isPointerDown = false;
      };

      // use pointer events if available, otherwise fallback to mouse and touch events
      if (typeof PointerEvent !== 'undefined') {
        doc.addEventListener('pointerup', handlePointerUp, true);
        doc.addEventListener('pointerdown', handlePointerDown, true);

        return () => {
          doc.removeEventListener('pointerup', handlePointerUp, true);
          doc.removeEventListener('pointerdown', handlePointerDown, true);
        };
      } else {
        doc.addEventListener('mousedown', handlePointerDown, true);
        doc.addEventListener('mouseup', handleMouseUp, true);

        doc.addEventListener('touchstart', handlePointerDown, true);
        doc.addEventListener('touchend', handleTouchEnd, true);

        return () => {
          doc.removeEventListener('mousedown', handlePointerDown, true);
          doc.removeEventListener('mouseup', handleMouseUp, true);

          doc.removeEventListener('touchstart', handlePointerDown, true);
          doc.removeEventListener('touchend', handleTouchEnd, true);
        };
      }
    }
  }, [clearComposedPath, isDisabled, recordComposedPath, ref]);

  return { lastComposedPath: composedPathRef };
}
